import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FormBuilder, FormGroup, FormArray } from '@angular/forms';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { combineLatest, Subject } from 'rxjs';
import { map, takeUntil, first, distinctUntilChanged, debounceTime } from 'rxjs/operators';

import { ApplicationService, SchoolNomineeInfo, NominationWindowInfo, RsvpSaveParameter, RsvpGuest, NominationFormSectionQuestion, RsvpQuestionSaveParameter, RsvpGuestSaveParameter, RsvpNominee } from 'services/application/application.service';
import { AwardsService, AwardProgramInfo } from 'services/awards/awards.service';
import { ErrorService } from 'services/error/error.service';
import { SettingsService } from 'services/settings/settings.service';
import { UserService, UserInfo } from 'services/user/user.service';
import { SplashService } from 'services/splash/splash.service';
import { DialogService } from 'services/dialogs/dialogs.service';

@Component({
    selector: 'app-rsvp',
    styleUrls: ['rsvp.component.scss'],
    templateUrl: 'rsvp.component.html',
})

export class RsvpComponent implements OnDestroy, OnInit {
    private _subrelease: Subject<void> = new Subject<void>();

    rsvpForm: FormGroup;
    GuestArray: FormArray;
    User: UserInfo;
    AwardInfo: AwardProgramInfo;
    CurrentSchoolNumber: string;
    CurrentSchoolYear: string;
    CurrentNominee: SchoolNomineeInfo;
    CurrentWindow: NominationWindowInfo;
    GradeLevelParam: string;
    NomineeTypeIDParam: number;
    MaxNumberOfGuests: number;
    BlankQuestions: NominationFormSectionQuestion[];
    TrackingBaseline: string;
    ChangesDetected: boolean;
    ViewReady: boolean = false;
    ValidForm: boolean = true;
    Nominees: SchoolNomineeInfo[] = [];

    constructor(
        private appSvc: ApplicationService,
        private awardSvc: AwardsService,
        private dialogSvc: DialogService,
        private errSvc: ErrorService,
        private route: ActivatedRoute,
        private settingsSvc: SettingsService,
        private userSvc: UserService,
        private splshSvc: SplashService,
        private fb: FormBuilder,
    ) { }

    ngOnDestroy() {
        this._subrelease.next();
        this._subrelease.complete();
    }

    ngOnInit() {
        this.userSvc.UserSubject.pipe(
            takeUntil(this._subrelease),
            first(u => !!u && !!u.AuthToken)
        ).subscribe(user => {
			this.User = user;

			if (user == null) return;

			this.settingsSvc.CurrentSchoolYearSubject.pipe(takeUntil(this._subrelease)).subscribe(year => {
				this.CurrentSchoolYear = year;

				let award$ = this.awardSvc.CurrentAwardsSub;
				let school$ = this.settingsSvc.CurrentSchoolSubject; //Only if developer/admin
				let windows$ = this.appSvc.GetNominationWindows(year);
				let params$ = this.route.params;

				combineLatest(award$, school$, windows$, params$).pipe(
					takeUntil(this._subrelease),
					map(_pck => {
						return {
							award: _pck[0],
							school: _pck[1],
							windows: _pck[2],
							params: _pck[3]
						};
					})
				).subscribe(packet1 => {
					this.AwardInfo = packet1.award;
					this.CurrentSchoolNumber = packet1.school.SchoolNumber;
					let win = packet1.windows;
					this.NomineeTypeIDParam = +packet1.params['NomineeType'];
					this.GradeLevelParam = packet1.params['GradeLevel'];

					this.appSvc.GetNominationsByWorkLocation(
						this.User.AuthToken,
						this.CurrentSchoolNumber,
						this.AwardInfo.AwardID,
						this.CurrentSchoolYear
					).pipe(takeUntil(this._subrelease)).subscribe(nominationInfo => {
						//put nominee and nomType together into a SchoolNomineeInfo
						for (let n of nominationInfo.NomineeType) {
							let nInfo = new SchoolNomineeInfo();

							nInfo.NomineeInfo = nominationInfo.Nominees.find(x => x.NomineeTypeID === n.NomineeTypeID && x.GradeLevel === n.GradeLevel);

							switch (n.GradeLevel) {
								case "STUDENTELEM":
									n.GradeDisplay = "Elementary School Nominee";
									break;
								case "STUDENTMIDDLE":
									n.GradeDisplay = "Middle School Nominee";
									break;
								case "STUDENTHIGH":
									n.GradeDisplay = "High School Nominee";
									break;
								default:
									n.GradeDisplay = null;
							}

							nInfo.NomineeType = n;
							this.Nominees.push(nInfo);

							if (nInfo.NomineeType.NomineeTypeID === this.NomineeTypeIDParam && nInfo.NomineeType.GradeLevel == this.GradeLevelParam) {
								this.CurrentNominee = nInfo;
							}
						}

						if (this.CurrentNominee != null && this.CurrentNominee.NomineeInfo != null) {
							this.CurrentWindow = win.find(w =>
								w.NomineeTypeID === this.CurrentNominee.NomineeType.NomineeTypeID &&
								w.SchoolYear === this.CurrentSchoolYear &&
								w.WindowTypeCode === 'RSVP' &&
								w.Open);

							if (this.CurrentWindow != null) {
								this.appSvc.GetRsvpByNominationID(
									this.User.AuthToken,
									this.User.CurrentRole,
									this.AwardInfo.AwardID,
									this.CurrentSchoolNumber,
									this.CurrentNominee.NomineeInfo.NominationID,
									this.CurrentSchoolYear
								).pipe(takeUntil(this._subrelease)).subscribe(rsvp => {
									this.MaxNumberOfGuests = rsvp.MaxGuestCount;

									//create base form
									this.rsvpForm = this.fb.group({
										NomineeAttendance: rsvp.NomineeAttendance,
										GuestList: this.fb.array([])
									});

									this.GuestArray = this.rsvpForm.get('GuestList') as FormArray;
									this.createBlankQuestions(rsvp);

									//add guests
									if (rsvp.GuestList != null && rsvp.GuestList.length > 0) {
										for (let g of rsvp.GuestList) {
											if (g.GuestNumber != null) {
												this.addGuest(null, g);
											}
										}
									}

									//track changes to form
									this.TrackingBaseline = JSON.stringify(this.rsvpForm.value);
									this.rsvpForm.valueChanges.pipe(
										takeUntil(this._subrelease),
										distinctUntilChanged(),
										debounceTime(500)
									).subscribe(value => {
										this.ChangesDetected = this.TrackingBaseline !== JSON.stringify(value);
									});

									this.ViewReady = true;
								}, error => this.errSvc.HandleServiceError(error));
							}
						}
					}, error => this.errSvc.HandleServiceError(error));
				}, error => this.errSvc.HandleServiceError(error));
			});
        }, error => this.errSvc.HandleServiceError(error));
    }

    addGuest(save: HTMLElement, guest: RsvpGuest) {
        if (guest == null) {
            guest = new RsvpGuest();
            guest.GuestNumber = this.GuestArray.length + 1;
            guest.QuestionList = this.BlankQuestions;
        }

        let guestForm = this.fb.group({
            GuestNumber: guest.GuestNumber,
            QuestionList: this.fb.array([])
        });
        let QuestionArray = guestForm.get('QuestionList') as FormArray;

        for (let q of this.BlankQuestions) {
            //if can't find blank questionID in question list, add it in
            if (guest.QuestionList.find(f => f.QuestionID === q.QuestionID) == null) {
                guest.QuestionList.push(q);
            }
        }

        guest.QuestionList.sort((a, b) => a.SortOrder > b.SortOrder ? 1 : -1);

        for (let q of guest.QuestionList) {
            let questionForm = this.fb.group({
                FormTemplateSectionID: [q.SectionID],
                FormTemplateSectionDetailID: [q.SectionDetailID],
                QuestionID: [q.QuestionID],
                QuestionText: [q.QuestionText],
                NarrativeText: [q.NarrativeText],
                TextLimit: [q.TextLimit],
                LowerTextLimit: [q.LowerTextLimit],
                TextLimitUnit: [q.TextLimitUnit],
                Response: [q.Response],
                OtherResponse: [q.OtherResponse],
                SortOrder: [q.SortOrder],
                ShowNumber: [q.ShowNumber],
                DigitOnly: [q.DigitOnly],
                QuestionType: [q.QuestionType],
                HasOptions: [q.HasOptions],
                CanSelectMultiple: [q.CanSelectMultiple],
                ResponseInvalid: [q.ResponseInvalid],
                InitiallyBlank: [q.InitiallyBlank],
                TextSizeCount: [q.TextSizeCount],
                Options: [q.Options],
                OtherSelected: [q.OtherSelected]
            });

            if (q.QuestionType === 'ShortResponse') {
                questionForm.get('Response').valueChanges.pipe(
                    takeUntil(this._subrelease),
                    distinctUntilChanged(),
                    debounceTime(250)
                )
                    .pipe(takeUntil(this._subrelease)).subscribe(value => {
                        if (questionForm.get('DigitOnly').value) {
                            if (/^\d+$/.test(value)) {
                                questionForm.get('ResponseInvalid').setValue(false, { emitEvent: false });
                            }
                            else {
                                questionForm.get('ResponseInvalid').setValue(true, { emitEvent: false });
                            }
                        }
                    });
            }

            if (q.QuestionType === 'LongResponse') {
                // This is a text-entry (paragraph) based entry. For those, we also subscribe to the value of the
                // response entry, so we can update the word counts for the overall formgroup.
                questionForm.get('Response').valueChanges.pipe(
                    takeUntil(this._subrelease),
                    distinctUntilChanged(),
                    debounceTime(250)
                )
                    .subscribe(value => {
                        const qval = questionForm.getRawValue();

                        // No need to update counts if there's no limit defined.
                        if (qval.TextLimit == null) {
                            return;
                        }

                        // Depending on the unit (1 = letters, 2 = words), calculate the count now.
                        const text = value || '';

                        let unitcount: number;
                        if (text == null || text.trim() == '') {
                            unitcount = 0;
                        }
                        else if (qval.TextLimitUnit === 1) {
                            unitcount = text.length;
                        }
                        else {
                            unitcount = text.match(/\S+/g).length;
                        }

                        // Update the count in the group
                        questionForm.get('TextSizeCount').setValue(unitcount, { emitEvent: false });
                    });

                // We also should trigger this updating if there's a response loaded.
                if (q.Response) {
                    this.UpdateTextCount(questionForm);
				}
				else {
					questionForm.get('TextSizeCount').setValue(0, { emitEvent: false });
				}
            }

            if (q.Response == null || q.Response == "" || q.Response === 'This response has already been provided by your supervisor' || q.Response === 'Your supervisor will provide this response') {
                questionForm.get('InitiallyBlank').setValue(true, { emitEvent: false });
            }

            if (q.HasOptions) {
                if (q.CanSelectMultiple) {
                    //value change event
                    questionForm.get('Response').valueChanges.pipe(
                        takeUntil(this._subrelease),
                        distinctUntilChanged(),
                        debounceTime(250)
                    ).subscribe((value: number[]) => {
                        //find the other option, can only have one other per question unless change db or use separators like in response
                        let other = q.Options.find(o => o.RequiresNote == true);

                        //if values include other value
                        if (other && value.includes(other.Value)) {
                            questionForm.get('OtherSelected').setValue(true, { emitEvent: false });
                            q.OtherSelected = true;
                        }
                        else {
                            //if not selected, set OtherSelected to false and clear OtherResponse
                            questionForm.get('OtherSelected').setValue(false, { emitEvent: false });
                            q.OtherSelected = false;
                            questionForm.get('OtherResponse').setValue(null, { emitEvent: false });
                            q.OtherResponse = null;
                        }
                    });

                    if (q.Response != null) {
                        let selectedOptions = q.Response.indexOf('|') < 0 ? [+q.Response] : q.Response.split('|').map(x => +x);

                        questionForm.get('Response').setValue(selectedOptions, { emitEvent: false });
                    }
                }
                else {
                    //value change event
                    questionForm.get('Response').valueChanges.pipe(
                        takeUntil(this._subrelease),
                        distinctUntilChanged(),
                        debounceTime(250)
                    ).subscribe((value: number) => {
                        //find the other option, can only have one other per question unless change db or use separators like in response
                        let other = q.Options.find(o => o.RequiresNote == true);

                        //if value = other, set OtherSelected to true
                        if (other && other.Value == value) {
                            questionForm.get('OtherSelected').setValue(true, { emitEvent: false });
                            q.OtherSelected = true;
                        }
                        else {
                            //if not selected, set OtherSelected to false and clear OtherResponse
                            questionForm.get('OtherSelected').setValue(false, { emitEvent: false });
                            q.OtherSelected = false;
                            questionForm.get('OtherResponse').setValue(null, { emitEvent: false });
                            q.OtherResponse = null;
                        }
                    });

                    //convert string to number
                    questionForm.get('Response').setValue(+q.Response, { emitEvent: false });
                }
            }

            QuestionArray.push(questionForm);
        }

        this.GuestArray.push(guestForm);
        if (this.ViewReady) {
            save.scrollIntoView({ behavior: 'smooth' });
        }
    }

    createBlankQuestions(rsvp: RsvpNominee) {
        this.BlankQuestions = [];
        let distinctQuestions: NominationFormSectionQuestion[] = [];

        for (let g of rsvp.GuestList) {
            for (let q of g.QuestionList) {
                //if don't find question, add it to list
                if (distinctQuestions.find(f => f.QuestionID === q.QuestionID) == null) {
                    distinctQuestions.push(q);
                }
            }
        }

        for (let q of distinctQuestions) {
            let deepCopy = new NominationFormSectionQuestion();
            deepCopy.SectionID = q.SectionID;
            deepCopy.SectionDetailID = q.SectionDetailID;
            deepCopy.QuestionID = q.QuestionID;
            deepCopy.QuestionText = q.QuestionText;
            deepCopy.NarrativeText = q.NarrativeText;
            deepCopy.TextLimit = q.TextLimit;
            deepCopy.LowerTextLimit = q.LowerTextLimit;
            deepCopy.TextLimitUnit = q.TextLimitUnit;
            deepCopy.Response = null;
            deepCopy.OtherResponse = null;
            deepCopy.SortOrder = q.SortOrder;
            deepCopy.ShowNumber = q.ShowNumber;
            deepCopy.DigitOnly = q.DigitOnly;
            deepCopy.QuestionType = q.QuestionType;
            deepCopy.HasOptions = q.HasOptions;
            deepCopy.CanSelectMultiple = q.CanSelectMultiple;
            deepCopy.ResponseInvalid = false;
            deepCopy.InitiallyBlank = true;
            deepCopy.TextSizeCount = q.TextSizeCount;
            deepCopy.Options = q.Options;
            deepCopy.OtherSelected = false;

            this.BlankQuestions.push(deepCopy);
        }
    }

    PackAndPost(packet: RsvpSaveParameter) {
        packet.AuthToken = this.User.AuthToken;
        packet.CurrentRole = this.User.CurrentRole;
        packet.AwardID = this.AwardInfo.AwardID;
        packet.WorkLocation = this.CurrentSchoolNumber;
		packet.NominationID = this.CurrentNominee.NomineeInfo.NominationID;
		packet.SchoolYear = this.CurrentSchoolYear;

        this.appSvc.SaveRsvpForm(packet).pipe(takeUntil(this._subrelease)).subscribe(res => {
            this.TrackingBaseline = JSON.stringify(this.rsvpForm.value);
            this.ChangesDetected = false;
            this.ValidForm = true;
            this.splshSvc.CloseSplash();
            this.splshSvc.SnackbarSuccess(`Submit successful`, 10000);
        }, error => {
            this.splshSvc.CloseSplash();
            this.splshSvc.SnackbarError(error.error, 10000);
        });
    }

    removeGuest(index: number) {
        this.GuestArray.removeAt(index);

        for (let g of this.GuestArray.controls) {
            if (g.value.GuestNumber > index + 1) {
                g.patchValue({ GuestNumber: g.value.GuestNumber - 1 });
            }
        }
    }

    submitRSVP(form: FormGroup) {
        if (form.invalid) {
            this.ValidForm = false;
            return;
        }

        let rsvpPacket = new RsvpSaveParameter();
        rsvpPacket.NomineeAttendance = form.value.NomineeAttendance;
        rsvpPacket.GuestList = [];
        
        for (let g of form.value.GuestList) {
            let guestPacket = new RsvpGuestSaveParameter();
            guestPacket.GuestNumber = g.GuestNumber;
            guestPacket.QuestionList = [];

            for (let q of g.QuestionList) {
                //get response from selection lists
                //is it an array (multiple options selected), or a single selection list (value), or just a string
                let response;
                if (Array.isArray(q.Response)) {
                    response = q.Response.join('|');
                }
                else if (q.Response && q.Response.Value) {
                    response = q.Response.Value;
                }
                else {
                    response = q.Response;
                }


                //don't send blank answers
                if (response == null || response == "" || response === 'This response has already been provided by your supervisor' || response === 'Your supervisor will provide this response') {
                    continue;
                }

                //validate response
                this.ValidForm = this.ValidateResponse(q, g.GuestNumber);
                if (!this.ValidForm) {
                    return;
                }

                //pack into save class
                let questionPacket: RsvpQuestionSaveParameter = {
                    FormTemplateSectionID: q.FormTemplateSectionID,
                    FormTemplateSectionDetailID: q.FormTemplateSectionDetailID,
                    QuestionID: q.QuestionID,
                    Response: response,
                    OtherResponse: q.OtherResponse
                };

                guestPacket.QuestionList.push(questionPacket);
            }

            rsvpPacket.GuestList.push(guestPacket);
        }

        this.PackAndPost(rsvpPacket);
    }

    UpdateCheckbox(event: MatCheckboxChange, Question: FormGroup) {
        if (!event || !Question) { return; }

        const newVal = (event.checked === true ? true : null);

        Question.get('Response').setValue(newVal);
    }

    UpdateTextCount(Question: FormGroup) {
        if (!Question) {
            return;
        }

        const qform = Question.getRawValue();

        if (qform.TextLimit == null) {
            return;
        } // No need to update counts if there's no limit defined.

        // Depending on the unit (1 = letters, 2 = words), calculate the count now.
        let unitcount = 0;

        if (qform.TextLimitUnit === 1) {
            unitcount = (qform.Response || '').length;
        } else {
            // Words, which we define simply as characters separated by at least 1 whitespace.
            const words = (qform.Response || '').match(/\S+/g);
            unitcount = !!words ? words.length : 0;
        }

        // Update the count in the group
        Question.get('TextSizeCount').setValue(unitcount);
    }

    ValidateResponse(quest: NominationFormSectionQuestion, guestNumber: number): boolean {
        if (quest.TextLimit != null) {
            if (quest.TextSizeCount > quest.TextLimit) {

                let message = `A text response to a question on this form is over the required text limit. ` +
                    `Please review the response size for the question shown below and revise the response to fit within the required text length before saving. ` +
                    `<br /><br /><strong>Guest Number: </strong>${guestNumber}<br /><br /><strong>Question: </strong><em>${quest.QuestionText}</em>`;

                this.dialogSvc.OpenMessageBoxDialog({
                    TitleText: 'Response Too Long',
                    MessageText: message,
                    OKText: 'OK',
                }).pipe(takeUntil(this._subrelease)).subscribe();

                return false;
            }
        }

        if (quest.LowerTextLimit != null) {
            if (quest.TextSizeCount > 0 && quest.TextSizeCount < quest.LowerTextLimit) {

                var message = `A text response to a question on this form is under the required text limit. ` +
                    `Please review the response size for the question shown below and revise the response to fit within the required text length before saving. ` +
                    `<br /><br /><strong>Guest Number: </strong>${guestNumber}<br /><br /><strong>Question: </strong><em>${quest.QuestionText}</em>`;

                this.dialogSvc.OpenMessageBoxDialog({
                    TitleText: 'Response Too Short',
                    MessageText: message,
                    OKText: 'OK'
                }).pipe(takeUntil(this._subrelease)).subscribe();

                return false;
            }
        }

        if (quest.DigitOnly) {
            if (!/^\d+$/.test(quest.Response)) {

                var message = `This question requires the answer to only contain numbers. ` +
                    `Please review the question and revise your answer to fit the requirements. ` +
                    `<br /><br / ><strong>Section: </strong>${guestNumber} ` +
                    `<br /><br /><strong>Question: </strong><em>${quest.QuestionText}</em> ` +
                    `<br /><br /><strong>Your Response: </strong><em>${quest.Response}</em>`;

                this.dialogSvc.OpenMessageBoxDialog({
                    TitleText: 'Invalid Response',
                    MessageText: message,
                    OKText: 'OK'
                }).pipe(takeUntil(this._subrelease)).subscribe();

                return false;
            }
        }

        if (quest.OtherSelected && (quest.OtherResponse == null || quest.OtherResponse == '')) {
            let response = quest.Options.find(o => o.RequiresNote === true).DisplayText;
            
            var message = `You selected a custom response for this question but did not provide your own response. ` +
                `Please review the question and revise your answer to fit the requirements. ` +
                `<br /><br / ><strong>Section: </strong>${guestNumber} ` +
                `<br /><br /><strong>Question: </strong><em>${quest.QuestionText}</em> ` +
                `<br /><br /><strong> Your Selection: </strong><em>${response}</em> ` +
                `<br /><br /><strong>Custom Response: </strong><em>${quest.OtherResponse}</em>`;

            this.dialogSvc.OpenMessageBoxDialog({
                TitleText: 'Custom Response Not Provided',
                MessageText: message,
                OKText: 'OK'
            }).pipe(takeUntil(this._subrelease)).subscribe();

            return false;
        }
        return true;
    }


}

